import {Component, HostListener, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';

import {TableauPage} from '@models/sidebar/response/tableau-page';
import {Organization} from '@models/organization/organization';
import {TranslateService} from '@ngx-translate/core';
import {TableauPageHelperService} from '@services/pages/tableau-page/tableau-page-helper.service';
import {TableauViz} from '@tableau/embedding-api';
import {TableauPageService} from '@services/left-menu/page/tableau-page/tableau-page.service';
import {NotificationService} from '@services/notification/notification.service';
import {TableauFilterItem} from '@models/pages/tableau-page/tableau-filter-item';

@Component({
    selector: 'scitis-tableau-page',
    templateUrl: './tableau-page.component.html',
    styleUrls: ['./tableau-page.component.scss'],
    viewProviders: [TableauViz],
    standalone: false
})
export class TableauPageComponent implements OnInit, OnChanges {

  @Input()
  public tableauPageConfiguration: TableauPage = new TableauPage('', '', true);

  @Input()
  public organization: Organization = new Organization();

  public embeddedViewUrl: string | null = null;

  //  Handle dashboard resizing
  public getScreenWidth: any;
  public getScreenHeight: any;
  public fallBackMessage = '';
  public showTableau: boolean = true;
  public tableauFilters: TableauFilterItem[] = [];
  public viewJWTAccessToken: string | null = null;

  constructor(private translate: TranslateService,
              private tableauPageHelperService: TableauPageHelperService,
              private tableauPageService: TableauPageService,
              private notification: NotificationService) {
    this.fallBackMessage = this.translate.instant('No tableau views configured. Go ahead and configure one!');
  }

  public get shouldShowTableau(): boolean {

    if (!this.organization?.tableauConfiguration?.url ||
        !this.organization?.tableauConfiguration?.siteName ||
        !this.tableauPageConfiguration?.tableauId) {
      return false;
    }

    if (!this.embeddedViewUrl || !this.viewJWTAccessToken) {
      return false;
    }

    return this.showTableau;
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.calculateDashboardSize();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes && (changes.organization || changes.tableauPageConfiguration)) {
      this.showTableau = false;
      this.getViewJWTAccessToken();
      setTimeout(() => {
        this.determineEmbeddedViewSource();
        this.showTableau = true;
      }, 100);

    }
  }

  ngOnInit(): void {
    this.calculateDashboardSize();
    this.determineEmbeddedViewSource();
  }

  private calculateDashboardSize(): void {
    const bufferSize = 25;
    this.getScreenWidth = window.innerWidth - bufferSize;
    this.getScreenHeight = (window.innerWidth - bufferSize) * 3 / 4;
  }

  private getParameters() {
    this.tableauFilters = [...this.tableauPageHelperService.generateTableauFilters(this.tableauPageConfiguration)];
  }

  private determineEmbeddedViewSource() {
    if (!this.organization?.tableauConfiguration?.url ||
        !this.organization?.tableauConfiguration?.siteName ||
        !this.tableauPageConfiguration?.tableauId) {
      this.embeddedViewUrl = '';
      return;
    }
    if (!this.organization.tableauConfiguration.url.endsWith('/')) {
      this.organization.tableauConfiguration.url = `${this.organization.tableauConfiguration.url}/`;
    }
    if (this.organization?.tableauConfiguration?.tableauCloud) {
      this.embeddedViewUrl = `${this.organization.tableauConfiguration.url}t/${this.organization.tableauConfiguration.siteName}/views/${this.tableauPageConfiguration?.tableauId}`;
    } else if (this.organization?.tableauConfiguration?.defaultSite) {
      this.embeddedViewUrl = `${this.organization.tableauConfiguration.url}views/${this.tableauPageConfiguration?.tableauId}`;
    } else {
      this.embeddedViewUrl = `${this.organization.tableauConfiguration.url}/t/${this.organization.tableauConfiguration.siteName}/views/${this.tableauPageConfiguration?.tableauId}`;
    }
    this.getParameters();
  }

  private getViewJWTAccessToken(): void {
    if (!this.organization.id || this.organization.id <= 0) {
      return;
    }
    this.viewJWTAccessToken = '';

    const viewObservable = this.tableauPageService.getViewAccessJwtToken(this.organization.id);

    viewObservable.subscribe({
      next: (result: string) => {
        if (result && result.length > 0) {
          this.viewJWTAccessToken = result[0];
        }
      },
      error: (error) => {
        this.notification.error('Cannot get JWT access token', error, 'tableauPage', true);
      }
    });
  }
}